import clsx from 'clsx'
import {cloneDeep, isEmpty, size} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {OverlayTrigger, Popover} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {CSSTransition} from 'react-transition-group'
import {ConfirmActionModal} from '../../../../../_gori/components'
import {blockPopUp, useDisabled} from '../../../../../_gori/helpers'
import useCancelToken from '../../../../../_gori/hooks/UseCancelToken'
import {
  Button,
  InputCheckNoFormik,
  SelectNoFormik,
  TableHeader,
  ValidationErrorModal,
} from '../../../../../_gori/partials/widgets'
import {useA1BatchesProvider} from '../../../../../_gori/providers/A1BatchesProvider'
import {A1MassUpdateModal} from '../../../a1'
import {
  CheapestServiceModal,
  ProcessCheapestServiceModal,
  ProcessEstimatedServiceModal,
} from '../../../orders'
import A1_CONSTANTS from '../../core/_const'
import A1BatchService from '../../core/_requests'
import {A1BatchesDetailTableFilter} from './A1BatchesDetailTableFilter'
import {useA1OrderFilter} from '../../hooks'
import {ActionAllModal} from '../../../common'
import {SET_UP_MODAL_ALL} from '../../../common/core/_const'
import {OrderStatus} from '../../../orders/core/_const'
import {isCreatedWithinLast30Days} from '../../../orders/core/_helpers'
import {ShipmentRefund} from '../../../shipments/core/_const'
import BatchService from '../../../batches/core/_requests'

type Props = {
  selectedList?: any
  reloadTable: any
  statistics: any
}

const A1BatchesDetailFilter: React.FC<Props> = ({statistics, selectedList, reloadTable}) => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const {setPageDisabled} = useDisabled()
  const [searchParams, setSearchParams] = useSearchParams()
  const {a1Batch} = useA1BatchesProvider()
  const [showPopover, setShowPopover] = useState<boolean>(false)
  const [loading, setLoading] = useState<{
    removeOrderA1Batch: boolean
    export: boolean
    loadingPrintLabel: boolean
  }>({
    removeOrderA1Batch: false,
    export: false,
    loadingPrintLabel: false,
  })
  const [validationErrors, setValidationErrors] = useState<any>()
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<{
    createLabel: boolean
    estimatedService: boolean
    processCheapestService: boolean
    cheapestService: boolean
    removeFromA1Batch: boolean
    massUpdateA1Batch: boolean
  }>({
    createLabel: false,
    estimatedService: false,
    processCheapestService: false,
    cheapestService: false,
    removeFromA1Batch: false,
    massUpdateA1Batch: false,
  })
  const [dataModal, setDataModal] = useState<{
    removeFromA1Batch: any
    massUpdateA1Batch: {ids: Array<number>; type: string}
  }>({
    removeFromA1Batch: [],
    massUpdateA1Batch: {ids: [], type: ''},
  })
  const {filters} = useA1OrderFilter()

  useEffect(() => {
    if (!selectedList.selectedMaster) {
      setSelectAll(false)
    }
  }, [selectedList.selected, selectedList.selectedMaster])

  const handleExport = async () => {
    try {
      setLoading((prev) => ({...prev, export: true}))
      const response = await A1BatchService.downloadOrdersA1Batch(a1Batch.id, {
        cancelToken: newCancelToken(),
        params: {
          ...filters,
        },
      })
      if (response) {
        const blob = new Blob([response.content], {type: response.type})
        let link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = response.file_name
        link.click()
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setLoading((prev) => ({...prev, export: false}))
    }
  }

  const getIdsByStatus = useCallback(
    (nameStatus) => {
      if (isEmpty(statistics)) return []
      const status: any = cloneDeep(statistics).find((item: any) => item.value === nameStatus)

      let orders: any
      if (!status) {
        orders = cloneDeep(statistics)
          .flatMap((item: any) => item.orders || [])
          .map((order: any) => ({original: order}))
      } else {
        orders = (status?.orders || []).map((order: any) => ({original: order}))
      }

      return orders
    },
    [statistics]
  )

  const filterOrdersByStatus = useCallback(
    (statusList) => {
      if (selectAll) {
        const currentStatus = searchParams.get('status')
        if (!currentStatus) {
          return statusList.flatMap((status) => getIdsByStatus(status))
        } else if (statusList.includes(currentStatus)) {
          return getIdsByStatus(currentStatus)
        }
      } else {
        return cloneDeep(selectedList.selected).flatMap((row) =>
          statusList.includes(row.original.status) ? [row] : []
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getIdsByStatus, searchParams, selectAll, selectedList.selected]
  )
  const orderIds = useMemo(() => {
    return {
      createLabel: filterOrdersByStatus([OrderStatus.OPEN]),
      massUpdateA1Batch: filterOrdersByStatus([OrderStatus.OPEN]),
      cheapestService: filterOrdersByStatus([OrderStatus.OPEN]),
      estimatedService: filterOrdersByStatus([OrderStatus.OPEN]),
      removeFromA1Batch: filterOrdersByStatus([OrderStatus.OPEN, OrderStatus.SHIPPED]),
      printLabel: filterOrdersByStatus([OrderStatus.SHIPPED]),
    }
  }, [filterOrdersByStatus])

  const contentRemoveFromA1Batch = useMemo(() => {
    return (
      <p className='fs-4 fw-normal'>
        <span>{intl.formatMessage({id: 'ARE_YOU_SURE_YOU_WANT_TO_REMOVE_THE'})}</span>
        <span>
          <b>
            {' ' +
              size(dataModal.removeFromA1Batch) +
              ' ' +
              (size(dataModal.removeFromA1Batch) === 1
                ? intl.formatMessage({id: 'ORDER'})
                : intl.formatMessage({id: 'ORDERS'})) +
              ' '}
          </b>
        </span>
        <span>{intl.formatMessage({id: 'FROM_THE_CURRENT_A1_BATCH'})}</span>
      </p>
    )
  }, [dataModal.removeFromA1Batch, intl])

  const handleRemoveOrderA1Batch = async () => {
    try {
      setLoading((prev) => ({...prev, removeOrderA1Batch: true}))
      setPageDisabled(true)
      const config = {cancelToken: newCancelToken()}

      const res = await A1BatchService.removeOrderA1Batch(
        a1Batch.id,
        {
          order_ids: dataModal.removeFromA1Batch.map((order: any) => order.original.id),
        },
        config
      )
      if (res) {
        toast.success(intl.formatMessage({id: res.message}))
        reloadTable()
        setShowModal((prev) => ({...prev, removeFromA1Batch: false}))
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setPageDisabled(false)
      setLoading((prev) => ({...prev, removeOrderA1Batch: false}))
    }
  }

  const handleCloseRemoveModal = () => {
    setDataModal((prev) => ({...prev, removeFromA1Batch: []}))
    setShowModal((prev) => ({...prev, removeFromA1Batch: false}))
  }

  const inputIncomplete = useMemo(() => {
    const handleIncompleteToggle = (e) => {
      const updatedParams = new URLSearchParams(searchParams)
      if (e.target.checked) {
        updatedParams.set('incomplete', 'true')
      } else {
        updatedParams.delete('incomplete')
      }
      setSearchParams(updatedParams)
    }

    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        show={showPopover}
        placement='top'
        overlay={
          <Popover className='min-w-400px mb-4'>
            <div className='p-5 rounded-2 border-start border-5 border-danger shadow text-danger fw-bold'>
              <div>{intl.formatMessage({id: 'SOME_ORDERS_IN_THIS_A1_BATCH_ARE_INCOMPLETE'})}</div>
              <div>
                {intl.formatMessage({
                  id: 'PLEASE_COMPLETE_THE_ORDER_INFORMATION_TO_PROCEED',
                })}
              </div>
            </div>
          </Popover>
        }
        delay={300}
      >
        <div>
          <div
            className='form-check form-check-danger ps-0 mb-0 mt-1'
            onMouseEnter={() => setShowPopover(true)}
            onMouseLeave={() => setShowPopover(false)}
          >
            <InputCheckNoFormik
              inputClassName={'border-danger'}
              label={intl.formatMessage({id: 'VIEW_INCOMPLETE_ORDERS'})}
              labelClassName={'text-danger'}
              checked={searchParams.get('incomplete') === 'true'}
              onChange={handleIncompleteToggle}
            />
          </div>
        </div>
      </OverlayTrigger>
    )
  }, [intl, searchParams, setSearchParams, showPopover])

  const handleMassUpdate = useCallback(
    (option) => {
      setShowModal((prev) => ({...prev, massUpdateA1Batch: true}))
      setDataModal((prev) => ({
        ...prev,
        massUpdateA1Batch: {
          ids: orderIds.massUpdateA1Batch.map((item: any) => {
            return item.original.id
          }),
          type: option.value,
        },
      }))
    },
    [orderIds.massUpdateA1Batch]
  )

  const handleResetInputIncomplete = useCallback(() => {
    if (searchParams.get('incomplete')) {
      const updatedParams = new URLSearchParams(searchParams)
      updatedParams.delete('incomplete')
      setSearchParams(updatedParams)
    }
  }, [searchParams, setSearchParams])

  const visibleTableHeaderActions = useMemo(
    () => !!selectedList.selected.length || !a1Batch?.orders_readied,
    [a1Batch?.orders_readied, selectedList.selected.length]
  )

  const handlePrintBatchLabel = useCallback(
    async (selected: any) => {
      let orderIds: any = []
      let refundOrderIds: any = []
      selected.forEach((row: any) => {
        if (!isCreatedWithinLast30Days(row.original?.latest_shipment?.created_at)) return

        if (
          row.original?.latest_shipment &&
          (row.original.latest_shipment.refund?.status === ShipmentRefund.SUBMITTED ||
            row.original.latest_shipment.refund?.status === ShipmentRefund.REFUNDED)
        ) {
          refundOrderIds.push(row.original.id)
        }
        orderIds.push(row.original.id)
      })
      if (orderIds.length === 0) {
        toast.warning(
          intl.formatMessage({
            id: 'PRINT_LABELS_WITHIN_LAST_30_DAYS_REQUIRED',
          })
        )
        return
      }
      if (orderIds.length === refundOrderIds.length) {
        toast.warning(
          intl.formatMessage({
            id: 'PRINT_LABELS_REFUNDED',
          })
        )
        return
      }
      if (orderIds.length > 0) {
        setLoading((prev) => ({...prev, loadingPrintLabel: true}))
        await BatchService.print(
          {
            order_ids: orderIds,
          },
          {
            cancelToken: newCancelToken(),
            params: {
              batch: a1Batch.name,
            },
          }
        )
          .then((response) => {
            if (blockPopUp(response.label_url)) {
              toast.warning(
                intl.formatMessage({
                  id: 'POP_UP_IS_BLOCKED',
                })
              )
            }
          })
          .catch((error) => {
            if (isCancel(error)) return
          })
          .finally(() => {
            setLoading((prev) => ({...prev, loadingPrintLabel: false}))
          })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, isCancel, newCancelToken]
  )

  const otherActionsOptions = useMemo(() => {
    return [
      {
        label: intl.formatMessage({id: 'REMOVE_FROM_A1_BATCH'}),
        value: 'removeFromA1Batch',
        disabled: isEmpty(orderIds.removeFromA1Batch),
      },
    ]
  }, [intl, orderIds.removeFromA1Batch])

  return (
    <>
      <A1BatchesDetailTableFilter
        className={clsx({
          'mb-5': !visibleTableHeaderActions,
        })}
        rightComponent={
          <div
            className={clsx('btn btn-advanced-tertiary cursor-pointer me-4 fw-bolder ms-4')}
            onClick={handleExport}
          >
            {intl.formatMessage({id: 'EXPORT_AS_CSV'})}
            {loading.export && (
              <span className='spinner-border spinner-border-sm align-middle ms-2' />
            )}
          </div>
        }
      />
      <div>
        {validationErrors && (
          <ValidationErrorModal
            handleClose={() => {
              setValidationErrors(undefined)
            }}
            response={validationErrors}
          />
        )}
        {showModal.createLabel && (
          <ActionAllModal
            show={showModal.createLabel}
            data={orderIds.createLabel}
            setUpModal={SET_UP_MODAL_ALL.CREATE_LABELS}
            handleClose={() => {
              reloadTable()
              setShowModal((prev) => ({...prev, createLabel: false}))
            }}
          />
        )}

        {showModal.cheapestService && (
          <CheapestServiceModal
            show={showModal.cheapestService}
            handleClose={() => setShowModal((prev) => ({...prev, cheapestService: false}))}
            orders={orderIds.cheapestService}
            confirm={() => setShowModal((prev) => ({...prev, processCheapestService: true}))}
          />
        )}
        {showModal.processCheapestService && (
          <ProcessCheapestServiceModal
            isSelectAll={selectAll}
            show={showModal.processCheapestService}
            data={orderIds.cheapestService}
            handleClose={() => {
              reloadTable()
              setShowModal((prev) => ({...prev, processCheapestService: false}))
            }}
          />
        )}
        {showModal.estimatedService && (
          <ProcessEstimatedServiceModal
            isSelectAll={selectAll}
            show={showModal.estimatedService}
            data={orderIds.estimatedService}
            handleClose={() => {
              reloadTable()
              setShowModal((prev) => ({...prev, estimatedService: false}))
            }}
          />
        )}
        {showModal.removeFromA1Batch && (
          <ConfirmActionModal
            show={showModal.removeFromA1Batch}
            title={intl.formatMessage({id: 'REMOVE_FROM_A1_BATCH'})}
            message={contentRemoveFromA1Batch}
            messagePrimary={intl.formatMessage({id: 'SUBMIT'})}
            messageSecondary={intl.formatMessage({id: 'CANCEL'})}
            handlePrimaryAction={handleRemoveOrderA1Batch}
            loadingPrimary={loading.removeOrderA1Batch}
            handleSecondaryAction={handleCloseRemoveModal}
            handleClose={handleCloseRemoveModal}
          />
        )}
        {showModal.massUpdateA1Batch && (
          <A1MassUpdateModal
            show={showModal.massUpdateA1Batch}
            handleClose={() => {
              setDataModal((prev) => ({...prev, massUpdateA1Batch: {ids: [], type: ''}}))
              setShowModal((prev) => ({...prev, massUpdateA1Batch: false}))
            }}
            data={dataModal.massUpdateA1Batch}
            handleSuccess={() => {
              reloadTable()
              setDataModal((prev) => ({...prev, massUpdateA1Batch: {ids: [], type: ''}}))
              setShowModal((prev) => ({...prev, massUpdateA1Batch: false}))
            }}
          />
        )}
        {visibleTableHeaderActions && (
          <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
            <TableHeader className='card-header border-0 d-flex align-items-center justify-content-between mb-5'>
              <div className='d-flex gap-5 justify-content-between flex-fill align-items-center'>
                <div
                  className={clsx('d-flex gap-5 justify-content-between flex-fill', {
                    invisible: selectedList.selected.length <= 0,
                  })}
                >
                  <div className='d-flex align-items-center gap-5'>
                    <div className='fw-bolder'>
                      <span className='me-2'>
                        {selectAll ? selectedList.total : selectedList.selected.length}
                      </span>
                      {intl.formatMessage({id: 'SELECTED'})}
                    </div>
                    {!selectAll && selectedList.selectedMaster && (
                      <div
                        onClick={() => setSelectAll(true)}
                        className='fw-bolder link-blue text-decoration-underline cursor-pointer'
                      >
                        {intl.formatMessage({id: 'SELECT_ALL'})} {selectedList.total}
                      </div>
                    )}
                    {selectAll && (
                      <div
                        onClick={() => setSelectAll(false)}
                        className='fw-bolder link-blue text-decoration-underline cursor-pointer'
                      >
                        {intl.formatMessage({id: 'CLEAR_SELECTION'})}
                      </div>
                    )}
                    {!isEmpty(orderIds.createLabel) && a1Batch?.readied && (
                      <Button
                        className='btn btn-advanced-secondary'
                        label={intl.formatMessage({id: 'CREATE_LABEL'})}
                        loadingText={intl.formatMessage({id: 'CREATE_LABEL'})}
                        event={() => setShowModal((prev) => ({...prev, createLabel: true}))}
                      />
                    )}

                    {!isEmpty(orderIds.printLabel) && (
                      <Button
                        className='btn btn-light-blue border border-blue'
                        label={intl.formatMessage({id: 'PRINT_LABELS'})}
                        loading={loading.loadingPrintLabel}
                        loadingText={intl.formatMessage({id: 'PRINT_LABELS'})}
                        event={() => handlePrintBatchLabel(orderIds.printLabel)}
                        disabled={loading.loadingPrintLabel}
                      />
                    )}

                    {!isEmpty(orderIds.massUpdateA1Batch) && (
                      <div className='cursor-pointer'>
                        <SelectNoFormik
                          emptyDefault={false}
                          className={'w-200px'}
                          value={{label: intl.formatMessage({id: 'MASS_UPDATE'}), value: ''}}
                          options={Object.values(A1_CONSTANTS.MASS_UPDATE)}
                          onChange={handleMassUpdate}
                          hasUseIntl={true}
                        />
                      </div>
                    )}
                    {!!selectedList.selected.length && (
                      <SelectNoFormik
                        emptyDefault={false}
                        className={'w-200px fw-bold fs-5'}
                        value={{label: intl.formatMessage({id: 'OTHER_ACTIONS'}), value: ''}}
                        options={otherActionsOptions}
                        onChange={(e) => {
                          switch (e.value) {
                            default:
                              setDataModal((prev) => ({
                                ...prev,
                                [e.value]: orderIds[e.value],
                              }))
                              setShowModal((prev) => ({...prev, [e.value]: true}))

                              break
                          }
                        }}
                      />
                    )}
                  </div>
                  <div className='d-flex gap-5 align-items-center'>
                    {!isEmpty(orderIds.cheapestService) && (
                      <Button
                        className='btn btn-advanced-secondary'
                        label={intl.formatMessage({id: 'CHEAPEST_SERVICE'})}
                        loadingText={intl.formatMessage({id: 'CHEAPEST_SERVICE'})}
                        event={() => setShowModal((prev) => ({...prev, cheapestService: true}))}
                      />
                    )}
                    {!isEmpty(orderIds.estimatedService) && (
                      <Button
                        className='btn btn-advanced-secondary'
                        label={intl.formatMessage({id: 'ESTIMATE_SERVICE'})}
                        loadingText={intl.formatMessage({id: 'ESTIMATE_SERVICE'})}
                        event={() => setShowModal((prev) => ({...prev, estimatedService: true}))}
                      />
                    )}
                  </div>
                </div>
                {a1Batch?.orders_readied ? handleResetInputIncomplete() : inputIncomplete}
              </div>
            </TableHeader>
          </CSSTransition>
        )}
      </div>
    </>
  )
}

export {A1BatchesDetailFilter}
